import React, { useState, useMemo, useRef } from "react";

import { HexColorPicker, HexColorInput } from "react-colorful";
import { Controller, FieldValues } from "react-hook-form";
import { debounce } from "lodash";

import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import { Box, Popper, Stack, useTheme } from "@mui/material";

import { FieldProps } from "./types";

const ColorPickerFieldImpl = ({
  control,
  watch,
  fieldName = "color",
  defaultValue,
}: FieldProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tempColor, setTempColor] = useState<string | null>(null);
  const theme = useTheme();

  // The debounced commit lives at COMPONENT level. It used to be a `useMemo` INSIDE
  // Controller's `render` callback — a hook in a callback, which react-hooks flags.
  // It happened to work only because Controller always invokes `render`; one early
  // return in there and the hook order breaks.
  // A latest-ref keeps the debounced function stable (created once) while always
  // calling the CURRENT `field.onChange`.
  const onChangeRef = useRef<(color: string) => void>(() => {});
  const debouncedOnChange = useMemo(
    () =>
      debounce((color: string) => {
        onChangeRef.current(color);
        setTempColor(null);
      }, 100),
    []
  );

  const currentColor = watch(fieldName) || defaultValue;
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    const parent = event.currentTarget.parentNode as HTMLElement;
    setAnchorEl(parent);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { onChange, value } }) => {
        // Keep the component-level debounce pointed at this render's onChange.
        onChangeRef.current = onChange;

        const handleColorPickerChange = (color: string) => {
          setTempColor(color);
          debouncedOnChange(color);
        };

        const displayColor = tempColor || value || currentColor;

        return (
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            width="100%"
            position="relative"
          >
            {/* preview color */}
            <Box
              className="absolute"
              onClick={handleClick}
              sx={{
                position: "absolute",
                left: 10,
                top: 10,
                zIndex: 1,
                width: 24,
                height: 24,
                borderRadius: 1,
                cursor: "pointer",
                border: `0.5px solid ${theme.palette.grey[300]}`,
                backgroundColor: displayColor,
                transition: "border-color 100ms linear",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                },
              }}
            />

            {/* Input hex */}
            <Box sx={{ flex: 1, position: "relative", ml: "0 !important" }}>
              <HexColorInput
                color={displayColor}
                onChange={onChange}
                prefixed
                style={{
                  width: "100%",
                  padding: "10px 12px 10px 44px",
                  fontSize: "16px",
                  height: "44px",
                  border: `1px solid ${theme.palette.grey[200]}`,
                  borderRadius: "8px",
                  backgroundColor: theme.palette.grey[50],
                  outline: "none",
                  fontFamily: "inherit",
                  cursor: "text",
                  transition: "border-color 150ms linear",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = theme.palette.brand[600];
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = theme.palette.grey[200];
                }}
              />

              {/* Color Picker Popper */}
              {anchorEl && (
                <ClickAwayListener onClickAway={handleClose}>
                  <Popper
                    placement="bottom-start"
                    open={!!anchorEl}
                    anchorEl={anchorEl}
                    disablePortal={false}
                    modifiers={[
                      // Flip above the field when there's no room below, reserving
                      // the fixed save-bar zone (~88px) at the bottom. The picker
                      // portals to <body> (z-index 1500), so the in-page save bar
                      // can't cover it via z-index — instead `tether: false` lets
                      // preventOverflow clamp the picker within (viewport − 88px),
                      // which is above the 65px bar, so it can never overlap it.
                      { name: 'flip', enabled: true, options: { fallbackPlacements: ['top-start'], padding: { bottom: 88 } } },
                      {
                        name: 'preventOverflow',
                        enabled: true,
                        options: { mainAxis: true, altAxis: true, tether: false, padding: { top: 8, bottom: 88, left: 8, right: 8 } },
                      },
                    ]}
                    sx={{
                      backgroundColor: theme.palette.white[100],
                      borderRadius: 2,
                      zIndex: 1500,
                      padding: 2,
                      boxShadow: theme.shadows[3],
                      // The house trigger→popover gap, in absolute px. Was `mt: 1` = 4px.
                      mt: '8px',
                      "& .react-colorful__saturation, .react-colorful__last-control":
                        {},
                      " & .react-colorful__saturation-pointer, .react-colorful__hue-pointer":
                        {
                          width: "16px",
                          height: "16px",
                        },
                    }}
                  >
                    <Box onClick={(e) => e.stopPropagation()}>
                      <HexColorPicker
                        color={displayColor}
                        onChange={handleColorPickerChange}
                      />
                    </Box>
                  </Popper>
                </ClickAwayListener>
              )}
            </Box>
          </Stack>
        );
      }}
    />
  );
};

/**
 * Re-exported under the generic signature so a caller's own form type flows through.
 * See the `FieldProps` docblock in `./types` for why the implementation above stays on the
 * default instantiation and why this assertion is the standard idiom rather than a
 * suppression.
 */
export const ColorPickerField = ColorPickerFieldImpl as <T extends FieldValues = FieldValues>(
  props: FieldProps<T>,
) => React.ReactElement | null
